import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import {
  ArrowRight,
  Calendar,
  Download,
  MoreVertical,
  Pencil,
  RefreshCw,
  Send,
  Trash2,
  XCircle,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FORM_CONTROL_FOCUS_CLASS } from '@/components/ui/form-control-styles'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Permit } from '@/components/guards/Permit'
import { usePermissions } from '@/hooks/usePermissions'
import { useQuickActions } from '@/hooks/useQuickActions'
import {
  ContractStatusBadge,
  ProspectBadge,
} from '@/components/shared/StatusBadges'
import { TruncatedTextTooltip } from '@/components/shared/TruncatedTextTooltip'
import { ContractStatusSelect } from '@/components/contracts/ContractStatusSelect'
import { formatCurrency, formatDate, formatPostalAddress } from '@/lib/format'
import { isContractEditable, totalAmountInclVat } from '@/lib/contracts'
import { cn } from '@/lib/utils'
import {
  CONTRACT_FREQUENCY_LABELS,
  JOB_STATUS,
  type ContractDetail,
  type Job,
} from '@/types'
import euroPositiveIcon from '@/assets/clients/detail/euro-positive.svg'
import calendarIcon from '@/assets/clients/detail/calendar.svg'
import clockIcon from '@/assets/contracts/detail/clock.svg'
import creditCardIcon from '@/assets/contracts/detail/credit-card.svg'
import refreshIcon from '@/assets/contracts/detail/refresh.svg'

const cardShadow =
  'shadow-[0px_8px_16px_-4px_rgba(38,45,66,0.1),0px_1px_4px_-4px_rgba(38,45,66,0.05)]'

const menuContentClass =
  'min-w-[200px] rounded-xl border border-[#e7e9ef] bg-white p-2 shadow-[0px_1px_4px_-1px_rgba(38,45,66,0.05),0px_1px_8px_-1px_rgba(38,45,66,0.1)]'
const menuItemClass =
  'cursor-pointer gap-3 rounded-lg px-4 py-3 text-sm font-normal leading-[1.4] text-[#171a20] normal-case focus:bg-[#f4f5f8] focus:text-[#171a20]'
const menuItemDestructiveClass =
  'cursor-pointer gap-3 rounded-lg px-4 py-3 text-sm font-normal leading-[1.4] text-[#d63b33] normal-case focus:bg-[#f4f5f8] focus:text-[#d63b33]'
const moreActionsTriggerClass = cn(
  'flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-xl border border-[#e7e9ef] bg-[#fbfbfc] p-2.5 text-[#171a20] transition-colors hover:border-[#ff5a1f] hover:bg-[#fff1ea] hover:text-[#ff5a1f] data-[state=open]:border-[#ff5a1f] data-[state=open]:bg-[#fff1ea] data-[state=open]:text-[#ff5a1f] data-[state=open]:hover:bg-[#fff1ea]',
  FORM_CONTROL_FOCUS_CLASS
)
const outlineBtnClass = cn(
  'h-10 gap-2 rounded-xl border-[#e7e9ef] bg-white px-3 py-3 text-sm font-medium leading-none text-[#171a20] normal-case hover:bg-[#f4f5f8]',
  FORM_CONTROL_FOCUS_CLASS
)
const primaryBtnClass = cn(
  'h-10 gap-2 rounded-xl border border-[#ff5a1f] bg-[#ff5a1f] px-3 py-3 text-sm font-medium leading-none text-white normal-case hover:bg-[#e6511c]',
  FORM_CONTROL_FOCUS_CLASS
)

const BILLING_PERIODS_PER_YEAR: Record<string, number> = {
  monthly: 12,
  quarterly: 4,
  biannual: 2,
  yearly: 1,
  one_off: 1,
}

function SoftBadge({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <span
      className={cn(
        'inline-flex h-6 items-center justify-center gap-1 rounded-lg px-2 py-1 text-xs font-medium leading-4 whitespace-nowrap',
        className
      )}
    >
      {children}
    </span>
  )
}

function MetricCell({
  iconSrc,
  iconClass,
  iconBoxClass,
  label,
  children,
  showDivider,
}: {
  iconSrc: string
  iconClass: string
  iconBoxClass: string
  label: string
  children: ReactNode
  showDivider?: boolean
}) {
  return (
    <div
      className={cn(
        'flex min-w-0 flex-1 items-start gap-3',
        showDivider && 'lg:border-r lg:border-[#b0b5c3] lg:pr-6'
      )}
    >
      <span
        className={cn(
          'flex size-11 shrink-0 items-center justify-center overflow-hidden rounded-xl p-3',
          iconBoxClass
        )}
      >
        <img
          src={iconSrc}
          alt=''
          className={cn('block max-w-none', iconClass)}
          width={20}
          height={20}
        />
      </span>
      <div className='flex min-w-0 flex-1 flex-col gap-1'>
        <p className='text-xs font-semibold uppercase leading-[1.4] text-[#3d434e]'>
          {label}
        </p>
        {children}
      </div>
    </div>
  )
}

function formatClockTime(value: string | null | undefined, locale: string) {
  if (!value) return null
  const [h, m] = value.split(':')
  if (h == null || Number.isNaN(Number(h))) return null
  const d = new Date()
  d.setHours(Number(h), Number(m ?? 0), 0, 0)
  return d.toLocaleTimeString(locale, {
    hour: 'numeric',
    minute: '2-digit',
  })
}

function formatInterventionWhen(job: Job, language: string) {
  const locale = language.startsWith('fr') ? 'fr-FR' : 'en-US'
  const d = new Date(`${job.scheduled_date}T00:00:00`)
  const datePart = Number.isNaN(d.getTime())
    ? formatDate(job.scheduled_date)
    : d.toLocaleDateString(locale, {
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      })
  const start = formatClockTime(job.start_time, locale)
  const end = formatClockTime(job.end_time, locale)
  if (start && end) return `${datePart}, ${start} – ${end}`
  if (start) return `${datePart}, ${start}`
  return datePart
}

function pickNextIntervention(jobs: Job[]): Job | null {
  const upcoming = jobs.filter(
    j =>
      j.status === JOB_STATUS.planned ||
      j.status === JOB_STATUS.assigned ||
      j.status === JOB_STATUS.in_progress
  )
  upcoming.sort((a, b) => {
    const da = `${a.scheduled_date}T${a.start_time ?? '00:00'}`
    const db = `${b.scheduled_date}T${b.start_time ?? '00:00'}`
    return da.localeCompare(db)
  })
  return upcoming[0] ?? null
}

// Figma 3475:169626 — contract information header (replaces the detail sidebar).
export function ContractDetailInfoSection({
  contract,
  jobs,
  onSchedule,
  onExtend,
  onExportPdf,
  onEdit,
  onSend,
  onTerminate,
  onDelete,
}: {
  contract: ContractDetail
  jobs: Job[]
  onSchedule: () => void
  onExtend: () => void
  onExportPdf: () => void
  onEdit: () => void
  onSend: () => void
  onTerminate: () => void
  onDelete: () => void
}) {
  const { t, i18n } = useTranslation('contracts')
  const navigate = useNavigate()
  const { canViewPath } = usePermissions()
  const { navigateToAddress } = useQuickActions()
  const c = contract

  const clientName = c.client?.name ?? c.client_name ?? t('detail.viewClient')
  const address = c.client ? formatPostalAddress(c.client) : ''
  const canViewClient = canViewPath(`/clients/${c.client_id}`)
  const canRenew =
    c.status === 'active' ||
    c.status === 'to_be_renewed' ||
    c.status === 'expired'
  const canSend = c.status === 'draft' || c.status === 'pending_signature'
  const canEdit = isContractEditable(c.status)
  const canSchedule = c.status !== 'terminated'
  const canTerminate = c.status === 'active' || c.status === 'to_be_renewed'
  const deletable = c.status === 'draft' || c.status === 'declined'
  const showMore = canEdit || canSend || canTerminate || deletable

  const annualInclVat = totalAmountInclVat(c)
  const periods = c.frequency ? BILLING_PERIODS_PER_YEAR[c.frequency] : null
  const periodAmount =
    annualInclVat != null && periods && periods > 0
      ? Math.round((annualInclVat / periods) * 100) / 100
      : null
  const completedCount = jobs.filter(
    j => j.status === JOB_STATUS.completed
  ).length
  const summary = c.summary
  const summaryNextDate = summary?.next_intervention?.scheduled_date ?? null
  const completedTotal =
    summary?.completed_interventions_count ?? completedCount
  const plannedTotal =
    summary?.planned_interventions_count ?? c.interventions_per_year ?? null
  const nextJob = pickNextIntervention(jobs)
  const titleLabel = c.title?.trim() || null

  const nextJobMeta = nextJob
    ? [
        nextJob.service_type,
        nextJob.assigned_technicians?.[0]?.full_name,
        formatPostalAddress(nextJob) || null,
      ]
        .filter(Boolean)
        .join(' · ')
    : ''

  return (
    <section
      className={cn(
        'flex w-full flex-col gap-4 overflow-hidden rounded-2xl border border-[#e7e9ef] p-6',
        cardShadow
      )}
      style={{
        backgroundImage:
          'linear-gradient(90deg, #ffffff 0%, #fffcfa 30%, #fff8f4 65%, #fff3ec 100%)',
      }}
    >
      <div className='flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between'>
        <div className='flex min-w-0 flex-1 flex-wrap items-center gap-4'>
          <h1 className='flex min-w-0 items-center gap-1 font-heading text-xl font-semibold leading-[1.2] text-[#171a20]'>
            <span className='truncate'>{clientName}</span>
            <ProspectBadge category={c.client?.category} />
          </h1>
          <Permit
            module='contracts'
            action='edit'
            fallback={<ContractStatusBadge status={c.status} />}
          >
            <ContractStatusSelect
              contractId={c.id}
              currentStatus={c.status}
              onTerminate={onTerminate}
              triggerClassName={FORM_CONTROL_FOCUS_CLASS}
            />
          </Permit>
        </div>

        <div className='flex flex-wrap items-center gap-3'>
          {canRenew && (
            <Permit module='contracts' action='edit'>
              <Button
                type='button'
                variant='outline'
                className={outlineBtnClass}
                onClick={onExtend}
              >
                <RefreshCw className='size-4' />
                {t('detail.actions.extend')}
              </Button>
            </Permit>
          )}
          <Button
            type='button'
            variant='outline'
            className={outlineBtnClass}
            onClick={onExportPdf}
          >
            <Download className='size-4' />
            {t('detail.actions.exportPdf')}
          </Button>
          {canSchedule && (
            <Permit module='contracts' action='edit'>
              <Button
                type='button'
                className={primaryBtnClass}
                onClick={onSchedule}
              >
                <Calendar className='size-4' />
                {t('detail.actions.planIntervention')}
              </Button>
            </Permit>
          )}
          {showMore && (
            <DropdownMenu>
              <TruncatedTextTooltip
                content={t('detail.moreActions')}
                delayDuration={0}
                alwaysShow
                closeOnPointerDown
                className='inline-flex shrink-0'
              >
                <DropdownMenuTrigger asChild>
                  <button
                    type='button'
                    aria-label={t('detail.moreActions')}
                    className={moreActionsTriggerClass}
                  >
                    <MoreVertical className='size-4' />
                  </button>
                </DropdownMenuTrigger>
              </TruncatedTextTooltip>
              <DropdownMenuContent
                align='end'
                sideOffset={8}
                className={menuContentClass}
              >
                {canEdit && (
                  <Permit module='contracts' action='edit'>
                    <DropdownMenuItem
                      className={menuItemClass}
                      onClick={onEdit}
                    >
                      <Pencil className='size-4 shrink-0' />
                      {t('detail.actions.edit')}
                    </DropdownMenuItem>
                  </Permit>
                )}
                {canSend && (
                  <Permit module='contracts' action='edit'>
                    <DropdownMenuItem
                      className={menuItemClass}
                      onClick={onSend}
                    >
                      <Send className='size-4 shrink-0' />
                      {t('detail.actions.sendToClient')}
                    </DropdownMenuItem>
                  </Permit>
                )}
                {canTerminate && (
                  <Permit module='contracts' action='edit'>
                    <DropdownMenuItem
                      className={menuItemDestructiveClass}
                      onClick={onTerminate}
                    >
                      <XCircle className='size-4 shrink-0' />
                      {t('detail.actions.terminate')}
                    </DropdownMenuItem>
                  </Permit>
                )}
                {deletable && (
                  <Permit module='contracts' action='delete'>
                    <DropdownMenuItem
                      className={menuItemDestructiveClass}
                      onClick={onDelete}
                    >
                      <Trash2 className='size-4 shrink-0' />
                      {t('detail.actions.delete')}
                    </DropdownMenuItem>
                  </Permit>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      <div className='flex flex-col gap-4'>
        <div className='flex flex-col gap-2 border-b border-dashed border-[#b0b5c3] pb-4'>
          <div className='flex flex-wrap items-center gap-1'>
            {address ? (
              <p className='text-sm font-medium leading-[1.4] text-[#171a20] normal-case'>
                {address}
              </p>
            ) : (
              <p className='text-sm font-medium leading-[1.4] text-[#757575] normal-case'>
                —
              </p>
            )}
            <span
              className='mx-1 size-1 shrink-0 rounded-full bg-[#b0b5c3]'
              aria-hidden
            />
            <button
              type='button'
              className={cn(
                'inline-flex items-center gap-2 rounded-xl px-1 py-0.5 text-xs font-medium leading-none text-[#ff5a1f] normal-case transition-colors hover:text-[#e6511c]',
                FORM_CONTROL_FOCUS_CLASS
              )}
              onClick={() =>
                navigateToAddress({
                  address: address || null,
                  latitude: c.client?.latitude ?? null,
                  longitude: c.client?.longitude ?? null,
                })
              }
            >
              {t('detail.direction')}
              <ArrowRight className='size-4' />
            </button>
          </div>

          <div className='flex flex-wrap items-center gap-2'>
            <SoftBadge className='bg-[#f4f5f8] text-[#171a20]'>
              {c.number ?? t('detail.fallbackNumber')}
            </SoftBadge>
            {titleLabel && (
              <SoftBadge className='bg-[#fff1ea] text-[#ff5a1f]'>
                {titleLabel}
              </SoftBadge>
            )}
            {canViewClient ? (
              <Link
                to={`/clients/${c.client_id}`}
                className={cn(
                  'inline-flex h-6 items-center gap-1 rounded-lg border border-[#e7e9ef] bg-white px-2 py-1 text-xs font-medium leading-4 text-[#ff5a1f] normal-case shadow-[0px_1px_4px_0px_rgba(38,45,66,0.05)] hover:bg-[#fff1ea]',
                  FORM_CONTROL_FOCUS_CLASS
                )}
              >
                {t('detail.customerProfile')}
                <ArrowRight className='size-3.5' />
              </Link>
            ) : (
              <SoftBadge className='border border-[#e7e9ef] bg-white text-[#ff5a1f]'>
                {t('detail.customerProfile')}
              </SoftBadge>
            )}
          </div>
        </div>

        <div className='flex flex-col gap-4 lg:flex-row lg:gap-6'>
          <MetricCell
            iconSrc={euroPositiveIcon}
            iconClass='h-[17.84px] w-[18.83px]'
            iconBoxClass='bg-[#ecfdf5]'
            label={t('detail.metrics.totalAmount')}
            showDivider
          >
            <div className='flex flex-wrap items-center gap-2'>
              <p className='font-heading text-base font-bold leading-[1.2] text-[#171a20]'>
                {annualInclVat != null
                  ? formatCurrency(annualInclVat, 'EUR', 2)
                  : '—'}
              </p>
              <p className='text-xs font-medium leading-[1.4] text-[#3d434e] normal-case'>
                {t('detail.metrics.inclVat')}
              </p>
            </div>
          </MetricCell>

          <MetricCell
            iconSrc={clockIcon}
            iconClass='size-[18.67px]'
            iconBoxClass='bg-[#ebf1fe]'
            label={t('detail.metrics.interventions')}
            showDivider
          >
            <div className='flex flex-wrap items-center gap-2'>
              <p className='font-heading text-base font-bold leading-[1.2] text-[#171a20]'>
                {plannedTotal != null
                  ? `${completedTotal}/${plannedTotal}`
                  : `${completedTotal}`}
              </p>
              <p className='text-xs font-medium leading-[1.4] text-[#3d434e] normal-case'>
                {t('detail.metrics.completed')}
              </p>
            </div>
          </MetricCell>

          <MetricCell
            iconSrc={creditCardIcon}
            iconClass='h-[15.33px] w-[20.33px]'
            iconBoxClass='bg-[#f9f0ff]'
            label={t('detail.metrics.billingFrequency')}
            showDivider
          >
            <div className='flex flex-wrap items-center gap-2'>
              <p className='font-heading text-base font-bold leading-[1.2] text-[#171a20]'>
                {c.frequency
                  ? (CONTRACT_FREQUENCY_LABELS[c.frequency] ?? c.frequency)
                  : '—'}
              </p>
              {periodAmount != null && c.frequency && (
                <p className='text-xs font-medium leading-[1.4] text-[#3d434e] normal-case'>
                  {t(`detail.metrics.periodAmount.${c.frequency}`, {
                    defaultValue: t('detail.metrics.periodAmount.yearly'),
                    amount: formatCurrency(periodAmount, 'EUR', 2),
                  })}
                </p>
              )}
            </div>
          </MetricCell>

          <MetricCell
            iconSrc={refreshIcon}
            iconClass='h-[17px] w-[20.33px]'
            iconBoxClass='bg-[#fff2e8]'
            label={t('detail.metrics.nextInterventionDate')}
          >
            <div className='flex flex-wrap items-center gap-2'>
              <p className='font-heading text-base font-bold leading-[1.2] text-[#171a20]'>
                {summaryNextDate ? formatDate(summaryNextDate) : '—'}
              </p>
              {summary?.next_intervention?.start_time && (
                <p className='text-xs font-medium leading-[1.4] text-[#3d434e] normal-case'>
                  {formatClockTime(
                    summary.next_intervention.start_time,
                    i18n.language
                  )}
                </p>
              )}
            </div>
          </MetricCell>
        </div>
      </div>

      {nextJob && (
        <div className='overflow-hidden rounded-2xl border border-[#ff9d75] bg-[#fff1ea]'>
          <div className='flex flex-col gap-3 rounded-l-2xl border-l-8 border-[#ff5a1f] px-6 py-4 sm:flex-row sm:items-start'>
            <span className='flex size-10 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-white p-2 shadow-[0px_1px_4px_0px_rgba(38,45,66,0.05)]'>
              <img
                src={calendarIcon}
                alt=''
                className='h-[18.67px] w-[17px] max-w-none'
                width={17}
                height={19}
              />
            </span>
            <div className='flex min-w-0 flex-1 flex-col gap-0.5'>
              <p className='font-heading text-base font-semibold leading-[1.4] text-[#171a20]'>
                {t('detail.nextIntervention.title', {
                  when: formatInterventionWhen(nextJob, i18n.language),
                })}
              </p>
              {nextJobMeta && (
                <p className='text-xs font-normal leading-4 text-[#3d434e] normal-case'>
                  {nextJobMeta}
                </p>
              )}
            </div>
            <Button
              type='button'
              className={cn(primaryBtnClass, 'shrink-0')}
              onClick={() => navigate(`/calendar?job=${nextJob.id}`)}
            >
              {t('detail.nextIntervention.seeInAgenda')}
              <ArrowRight className='size-4' />
            </Button>
          </div>
        </div>
      )}
    </section>
  )
}
